import React, { useState, useEffect } from "react";
import { Button, Input, Table, Popconfirm, Spin, Select } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { TLocation as StorageLocation } from "~/types";
import { NumberInput } from "../components/number_input";
import {
  useCreateLocation,
  useDeleteLocation,
  useGetLocations,
  useUpdateLocation,
  useGetStorageSettings,
  useUpdateStorageSettings,
} from "~/services/location";
import { useNotification } from "~/hooks";

export const StorageTab: React.FC = () => {
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationRate, setNewLocationRate] = useState<number>(0);
  const [newLocationFee, setNewLocationFee] = useState<number>(0);
  const [newLocationCubicFeet, setNewLocationCubicFeet] = useState<number>(0);
  const [modifiedLocations, setModifiedLocations] = useState<{
    [id: string]: Partial<StorageLocation>;
  }>({});
  const [tempChildren, setTempChildren] = useState<{
    [parentId: string]: Partial<StorageLocation>;
  }>({});
  const [initialLocations, setInitialLocations] = useState<StorageLocation[]>(
    []
  );
  const [storageBufferPercentage, setStorageBufferPercentage] =
    useState<number>(15);
  const [billingPeriodRounding, setBillingPeriodRounding] =
    useState<string>("Round Up (Always)");
  const [settingsChanged, setSettingsChanged] = useState<boolean>(false);

  const notification = useNotification();
  const {
    data: fetchedLocations,
    isLoading: isLocationsLoading,
    isFetching: isLocationsFetching,
  } = useGetLocations();
  const { data: storageSettings, isLoading: isSettingsLoading } =
    useGetStorageSettings();
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();
  const updateSettingsMutation = useUpdateStorageSettings();

  useEffect(() => {
    if (fetchedLocations?.items) {
      setInitialLocations(fetchedLocations.items);
    }
  }, [fetchedLocations]);

  useEffect(() => {
    if (storageSettings) {
      setStorageBufferPercentage(storageSettings.storageBufferPercentage);
      setBillingPeriodRounding(storageSettings.billingPeriodRounding);
      setSettingsChanged(false);
    }
  }, [storageSettings]);

  const hasChanges = (id: string): boolean => {
    const modified = modifiedLocations[id];
    if (!modified) return false;
    const original = findLocationById(initialLocations, id);
    if (!original) return true;
    return (
      (modified.ratePerCubicFt !== undefined &&
        modified.ratePerCubicFt !== original.ratePerCubicFt) ||
      (modified.minimumFee !== undefined &&
        modified.minimumFee !== original.minimumFee) ||
      (modified.minimumCubicFeet !== undefined &&
        modified.minimumCubicFeet !== original.minimumCubicFeet) ||
      (modified.name !== undefined && modified.name !== original.name)
    );
  };

  const findLocationById = (
    locations: StorageLocation[],
    id: string
  ): StorageLocation | undefined => {
    for (const loc of locations) {
      if (loc.id === id) return loc;
      if (loc.children) {
        const found = findLocationById(loc.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  const handleChange = (
    id: string,
    field: keyof StorageLocation,
    value: string | number | null,
    isTemp: boolean = false,
    parentId?: string
  ) => {
    if (value === null) return;
    if (isTemp && parentId) {
      setTempChildren((prev) => ({
        ...prev,
        [parentId]: { ...prev[parentId], [field]: value },
      }));
    } else {
      setModifiedLocations((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: value },
      }));
    }
  };

  const handleCancelEdit = (id: string) => {
    setModifiedLocations((prev) => {
      const newModified = { ...prev };
      delete newModified[id];
      return newModified;
    });
  };

  const handleCancelAddChild = (parentId: string) => {
    setTempChildren((prev) => {
      const newTemp = { ...prev };
      delete newTemp[parentId];
      return newTemp;
    });
  };

  const handleSave = (record: StorageLocation) => {
    const modifiedLocation = modifiedLocations[record.id];
    if (!modifiedLocation) return;

    const updateData = {
      ratePerCubicFt: modifiedLocation.ratePerCubicFt
        ? Number(modifiedLocation.ratePerCubicFt)
        : Number(record.ratePerCubicFt),
      minimumFee: modifiedLocation.minimumFee
        ? Number(modifiedLocation.minimumFee)
        : Number(record.minimumFee),
      minimumCubicFeet: modifiedLocation.minimumCubicFeet
        ? Number(modifiedLocation.minimumCubicFeet)
        : Number(record.minimumCubicFeet),
      name: modifiedLocation.name ?? record.name,
    };

    updateMutation.mutate(
      { id: record.id, params: updateData },
      {
        onSuccess: () => {
          notification.success({
            message: "Location updated",
            description: "Storage location was successfully updated.",
          });
          setModifiedLocations((prev) => {
            const newModified = { ...prev };
            delete newModified[record.id];
            return newModified;
          });
          setInitialLocations((prev) =>
            updateLocationInTree(prev, record.id, updateData)
          );
        },
        onError: (error: any) => {
          notification.error({
            message: "Update failed",
            description: error.message || "Failed to update storage location.",
          });
        },
      }
    );
  };

  const updateLocationInTree = (
    locations: StorageLocation[],
    id: string,
    updateData: Partial<StorageLocation>
  ): StorageLocation[] => {
    return locations.map((loc) => {
      if (loc.id === id) {
        return { ...loc, ...updateData };
      }
      if (loc.children) {
        return {
          ...loc,
          children: updateLocationInTree(loc.children, id, updateData),
        };
      }
      return loc;
    });
  };

  const addChildLocation = (parentId: string) => {
    const tempId = `temp-${Date.now()}`;
    setTempChildren((prev) => {
      const newTemp = { ...prev };
      Object.keys(newTemp).forEach((key) => {
        if (key === parentId) delete newTemp[key];
      });
      newTemp[parentId] = {
        id: tempId,
        name: "",
        ratePerCubicFt: 0,
        minimumFee: 0,
        minimumCubicFeet: 0,
        parentId,
        children: [],
      };
      return newTemp;
    });
  };

  const handleSaveNewChild = (parentId: string) => {
    const tempChild = tempChildren[parentId];
    if (!tempChild || !tempChild.name?.trim()) {
      notification.warning({
        message: "Invalid input",
        description: "Please enter a location name.",
      });
      return;
    }

    createMutation.mutate(
      {
        name: tempChild.name,
        ratePerCubicFt: tempChild.ratePerCubicFt ?? 0,
        minimumFee: tempChild.minimumFee ?? 0,
        minimumCubicFeet: tempChild.minimumCubicFeet ?? 0,
        parentId: tempChild?.parentId || undefined,
      },
      {
        onSuccess: (newChild: StorageLocation) => {
          notification.success({
            message: "Child location created",
            description: "New child location was successfully created.",
          });
          setInitialLocations((prev) =>
            addChildToParent(prev, tempChild.parentId!, newChild)
          );
          setTempChildren((prev) => {
            const newTemp = { ...prev };
            delete newTemp[parentId];
            return newTemp;
          });
        },
        onError: (error: any) => {
          notification.error({
            message: "Creation failed",
            description: error.message || "Failed to create child location.",
          });
        },
      }
    );
  };

  const addChildToParent = (
    locations: StorageLocation[],
    parentId: string,
    child: StorageLocation
  ): StorageLocation[] => {
    return locations.map((loc) => {
      if (loc.id === parentId) {
        return {
          ...loc,
          children: [...(loc.children || []), child],
        };
      }
      if (loc.children) {
        return {
          ...loc,
          children: addChildToParent(loc.children, parentId, child),
        };
      }
      return loc;
    });
  };

  const deleteLocation = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        notification.success({
          message: "Location deleted",
          description: "Storage location was successfully deleted.",
        });
        setInitialLocations((prev) => removeLocationFromTree(prev, id));
        setTempChildren((prev) => {
          const newTemp = { ...prev };
          delete newTemp[id];
          return newTemp;
        });
      },
      onError: (error: any) => {
        notification.error({
          message: "Delete failed",
          description: error.message || "Failed to delete storage location.",
        });
      },
    });
  };

  const removeLocationFromTree = (
    locations: StorageLocation[],
    id: string
  ): StorageLocation[] => {
    return locations
      .filter((loc) => loc.id !== id)
      .map((loc) => {
        if (loc.children) {
          return {
            ...loc,
            children: removeLocationFromTree(loc.children, id),
          };
        }
        return loc;
      });
  };

  const addNewRootLocation = () => {
    if (!newLocationName.trim()) {
      notification.warning({
        message: "Invalid input",
        description: "Please enter a location name.",
      });
      return;
    }

    createMutation.mutate(
      {
        name: newLocationName,
        ratePerCubicFt: newLocationRate,
        minimumFee: newLocationFee,
        minimumCubicFeet: newLocationCubicFeet,
      },
      {
        onSuccess: (newLocation: StorageLocation) => {
          notification.success({
            message: "Location created",
            description: "New storage location was successfully created.",
          });
          setInitialLocations((prev) => [...prev, newLocation]);
          setNewLocationName("");
          setNewLocationRate(0);
          setNewLocationFee(0);
          setNewLocationCubicFeet(0);
        },
        onError: (error: any) => {
          notification.error({
            message: "Creation failed",
            description: error.message || "Failed to create storage location.",
          });
        },
      }
    );
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(
      {
        storageBufferPercentage,
        billingPeriodRounding,
      },
      {
        onSuccess: () => {
          notification.success({
            message: "Settings updated",
            description:
              "Storage calculation settings were successfully updated.",
          });
          setSettingsChanged(false);
        },
        onError: (error: any) => {
          notification.error({
            message: "Update failed",
            description: error.message || "Failed to update storage settings.",
          });
        },
      }
    );
  };

  const flattenLocations = (
    locations: StorageLocation[] | undefined,
    level: number = 0
  ): any[] => {
    if (!locations) return [];
    const result: any[] = [];
    locations.forEach((location) => {
      if (!location) return;
      const tempChild = tempChildren[location.id];
      result.push({
        ...location,
        ...modifiedLocations[location.id],
        level,
        hasChildren: !!location.children && location.children.length > 0,
        isExpanded: false,
        isNew: false,
      });
      if (tempChild) {
        result.push({
          ...tempChild,
          level: level + 1,
          hasChildren: false,
          isExpanded: false,
          isNew: true,
        });
      }
      if (location.children) {
        result.push(...flattenLocations(location.children, level + 1));
      }
    });
    return result;
  };

  const columns: ColumnsType<any> = [
    {
      title: "Storage Location",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <div
          className="flex items-center"
          style={{ paddingLeft: `${record.level * 20}px` }}
        >
          <Input
            value={
              record.isNew ? name : modifiedLocations[record.id]?.name ?? name
            }
            onChange={(e) =>
              handleChange(
                record.id,
                "name",
                e.target.value,
                record.isNew,
                record.isNew ? record.parentId : undefined
              )
            }
            placeholder="Enter location name"
          />
        </div>
      ),
    },
    {
      title: "Rate (per cubic ft)",
      dataIndex: "ratePerCubicFt",
      key: "ratePerCubicFt",
      render: (value: number, record: any) => (
        <NumberInput
          value={
            record.isNew
              ? value
              : modifiedLocations[record.id]?.ratePerCubicFt ?? value
          }
          prefix="$"
          onChange={(newValue) =>
            handleChange(
              record.id,
              "ratePerCubicFt",
              newValue,
              record.isNew,
              record.isNew ? record.parentId : undefined
            )
          }
        />
      ),
    },
    {
      title: "Minimum Fee",
      dataIndex: "minimumFee",
      key: "minimumFee",
      render: (value: number, record: any) => (
        <NumberInput
          value={
            record.isNew
              ? value
              : modifiedLocations[record.id]?.minimumFee ?? value
          }
          prefix="$"
          onChange={(newValue) =>
            handleChange(
              record.id,
              "minimumFee",
              newValue,
              record.isNew,
              record.isNew ? record.parentId : undefined
            )
          }
        />
      ),
    },
    {
      title: "Minimum Cubic Feet",
      dataIndex: "minimumCubicFeet",
      key: "minimumCubicFeet",
      render: (value: number, record: any) => (
        <NumberInput
          value={
            record.isNew
              ? value
              : modifiedLocations[record.id]?.minimumCubicFeet ?? value
          }
          suffix="cu ft"
          onChange={(newValue) =>
            handleChange(
              record.id,
              "minimumCubicFeet",
              newValue,
              record.isNew,
              record.isNew ? record.parentId : undefined
            )
          }
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: any) => {
        if (record.isNew) {
          return (
            <div className="flex space-x-2">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={() => handleSaveNewChild(record.parentId)}
                loading={createMutation.isPending}
                disabled={!record.name?.trim()}
              >
                Save
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleCancelAddChild(record.parentId)}
              >
                Cancel
              </Button>
            </div>
          );
        }
        if (hasChanges(record.id)) {
          return (
            <div className="flex space-x-2">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={() => handleSave(record)}
                loading={
                  updateMutation.isPending &&
                  updateMutation.variables?.id === record.id
                }
              >
                Save
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleCancelEdit(record.id)}
              >
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <div className="flex space-x-2">
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => addChildLocation(record.id)}
              size="small"
              disabled={!!tempChildren[record.id]}
            >
              Add Child
            </Button>
            <Popconfirm
              title="Delete Storage Location"
              description={`Are you sure you want to delete "${record.name}"?`}
              onConfirm={() => deleteLocation(record.id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: deleteMutation.isPending }}
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={
                  deleteMutation.isPending &&
                  deleteMutation.variables === record.id
                }
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  if (isLocationsLoading || isLocationsFetching || isSettingsLoading) {
    return <Spin size="large" className="flex justify-center p-6" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Storage Rates by Location</h3>
        <p className="text-gray-500 mb-4">
          Configure rates for different storage locations with parent-child
          hierarchy
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={flattenLocations(initialLocations)}
        rowKey="id"
        pagination={false}
        className="bg-white"
        expandable={{
          expandedRowRender: (record) => (
            <Table
              columns={columns}
              dataSource={flattenLocations(
                record.children || [],
                record.level + 1
              )}
              rowKey="id"
              pagination={false}
              showHeader={false}
            />
          ),
          rowExpandable: (_) => false,
        }}
      />

      <div className="p-4 border rounded-lg bg-gray-50">
        <h4 className="font-medium mb-3">Add New Storage Location</h4>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Input
            placeholder="Location name"
            className="col-span-2"
            value={newLocationName}
            onChange={(e) => setNewLocationName(e.target.value)}
          />
          <NumberInput
            value={newLocationRate}
            prefix="$"
            onChange={(value) => setNewLocationRate(value ? Number(value) : 0)}
          />
          <NumberInput
            value={newLocationFee}
            prefix="$"
            onChange={(value) => setNewLocationFee(value ? Number(value) : 0)}
          />
          <NumberInput
            value={newLocationCubicFeet}
            suffix="cu ft"
            onChange={(value) =>
              setNewLocationCubicFeet(value ? Number(value) : 0)
            }
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="primary"
            size="small"
            onClick={addNewRootLocation}
            loading={createMutation.isPending}
            disabled={!newLocationName.trim()}
          >
            Add Location
          </Button>
        </div>
      </div>

      <div className="p-4 border rounded-lg bg-gray-50">
        <h4 className="font-medium mb-3">Storage Calculation Settings</h4>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Storage Buffer Percentage (%)
            </label>
            <NumberInput
              value={storageBufferPercentage}
              suffix="%"
              onChange={(value) => {
                setStorageBufferPercentage(value ? Number(value) : 0);
                setSettingsChanged(true);
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing Period Rounding
            </label>
            <Select
              className="w-full"
              value={billingPeriodRounding}
              onChange={(value) => {
                setBillingPeriodRounding(value);
                setSettingsChanged(true);
              }}
            >
              <Select.Option value="ROUND_UP">Round Up (Always)</Select.Option>
              <Select.Option value="ROUND_TO_NEAREST">
                Round to Nearest
              </Select.Option>
              <Select.Option value="ROUND_DOWN">Round Down</Select.Option>
            </Select>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            type="primary"
            size="small"
            onClick={handleSaveSettings}
            disabled={!settingsChanged}
            loading={updateSettingsMutation.isPending}
            icon={<SaveOutlined />}
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};
